import { addSample, emptySums, scanSums, solveLevel, solvePlane } from './surface-probe'

/**
 * The floor, and everything that turns one depth frame's cells into "ground" or
 * "relief standing on the ground".
 *
 * ARCore's floor is not flat: measured noise puts ~9% of cells more than 6 mm
 * proud with peaks around 10 mm. A box standing on that reads the bumps as
 * terrain, which drowns the thing the demo is meant to show — what happens when
 * a *real object* arrives.
 *
 * Two answers live here.
 *
 * `stripToRelief` is the one the demo ships with: given a plane that came from
 * somewhere trustworthy (WebXR hit-test), delete every cell that is not
 * standing proud of it. The floor stops being sensed geometry and becomes an
 * analytic plane, and depth keeps the one job it is good at.
 *
 * `lockGround` is the other: fit a plane to the dominant floor in the depth
 * frame itself, snap every nearby cell onto it, and fill holes surrounded by
 * floor. It is kept because it is the only mode that measures the sensed floor
 * rather than replacing it, and because four passes of tuning it are what
 * established that its residual is the sensor's noise floor and not the
 * algorithm's. See docs/MEASUREMENTS.md §5.
 *
 * This module writes `positions` and `metres` in place. They are frame-scoped
 * scratch buffers owned by the caller and rebuilt from the depth buffer every
 * frame; copying them per frame at 30 fps is the wrong trade. Indices are
 * derived from `cells` and the grid dimensions, so the reads are asserted.
 */

/** Immutable at its boundary: every producer builds a fresh one. */
export interface GroundPlane {
  readonly height: number
  readonly slopeX: number
  readonly slopeZ: number
  readonly originX: number
  readonly originZ: number
  /** RMS residual of the inliers, metres. What the floor's real roughness was. */
  readonly rms: number
  readonly inliers: number
}

export interface GroundReport {
  readonly plane: GroundPlane | null
  /** Cells accounted for by the floor. */
  readonly locked: number
  /** Cells that had no depth and were given the plane's. */
  readonly filled: number
  /** Cells carrying depth before filling. */
  readonly valid: number
  /** Of the locked cells, how many were corrected along the ray rather than in Y. */
  readonly radial: number
  /** Cells above the band stripped as isolated noise spikes. */
  readonly spikes: number
}

/** Seed disc, around the point the caller believes is floor. */
const SEED_RADIUS = 0.4

/** Cells beyond this from the seed are never touched. */
const PLANE_RADIUS = 1.5

/**
 * Successively tighter inlier bands. The first is loose enough to pull the fit
 * off the seed disc and out across the whole floor; the last is tight enough
 * that a 25 mm object cannot influence it.
 */
const REFINE_BANDS = [0.06, 0.03, 0.012]

/**
 * Lock band, asymmetric on purpose.
 *
 * A symmetric 12 mm band is only 2σ against a measured ~6 mm noise floor, so
 * roughly a quarter of genuinely flat floor falls outside it and stays lumpy.
 * Widening it symmetrically would start swallowing short real objects, so only
 * the lower half is widened: a cell *below* the floor plane is never an object,
 * because objects stand up. That captures the entire lower tail for free while
 * the upper limit stays well under the climb threshold's blind spot.
 */
const LOCK_BAND_BELOW = 0.04
const LOCK_BAND_ABOVE = 0.012

/**
 * A cell above the band with every neighbour already locked is a noise spike,
 * not the edge of an object: real objects arrive as contiguous blobs several
 * cells across, and a 25 mm object at ~9.5 mm cell spacing still occupies a
 * solid patch rather than a scatter. One pass, so a stripped cell can never
 * become a neighbour for the next one and eat inward into a real object.
 *
 * The ceiling was 30 mm, which is above the 25 mm reference object: a cell on
 * that object's sloped rim, ringed by locked floor, qualified — and that rim is
 * precisely the wall that shoves a box. 18 mm keeps it below anything the demo
 * is meant to collide with, and 4-of-4 neighbours means a spike must be fully
 * surrounded rather than merely on an edge. Do not raise the cap above the
 * smallest object you intend to collide with.
 */
const SPIKE_MAX = 0.018
const SPIKE_MIN_NEIGHBOURS = 4

/**
 * A ray correction larger than this is not a 12 mm floor cell being nudged, it
 * is a near-grazing ray whose intersection has run away. Those fall back to the
 * vertical snap, which is bounded by the lock band by construction.
 */
const MAX_RAY_SHIFT = 0.1

/**
 * Ceiling on how far a ray correction may move a cell *sideways*.
 *
 * The radial move is the geometrically correct one — depth is the only free
 * parameter a camera ray has — but this is a grid, and two things downstream
 * assume the grid stays regular in XZ: the quad emitter indexes by grid
 * position, and the body solver bins cells by XZ distance. At a grazing angle a
 * 6 mm height error is a ~35 mm lateral slide, roughly four cell widths, so
 * unconstrained ray snapping piles cells up and tears gaps between them. The
 * measured cost of that was bodies losing their support fit on 42% of frames
 * and a mesh that folded instead of flattening.
 *
 * Half a cell at the ~9.5 mm spacing this grid runs at. Beyond it the vertical
 * snap is used, which is wrong in XZ but keeps the sampling uniform — and a
 * uniform grid at the right height is what everything downstream was built for.
 */
const MAX_LATERAL_SHIFT = 0.005

const FILL_PASSES = 6
const FILL_MIN_NEIGHBOURS = 2
const MAX_FILL_METRES = 5

const GROUND = 1
const PENDING = 2

/**
 * Weight given to a freshly fitted plane against the one carried from last
 * frame.
 *
 * The demo is otherwise memoryless by design, and this is the one exception:
 * three floats, not geometry. The reason is that a plane re-derived from
 * scratch every frame moves every frame — the fit is over a different set of
 * inlier cells each time, and its seed follows the camera — so the floor slides
 * under everything standing on it. Cells snapped onto a moving plane look
 * exactly as unsteady as cells that were never snapped at all.
 *
 * Low enough to take several frames to accept a change, high enough to follow a
 * real one within a fraction of a second.
 */
const PLANE_BLEND = 0.15

/**
 * Past this the new fit is not the same surface — a re-localisation, or the
 * camera has swung onto a different plane — so it replaces rather than blends.
 */
const PLANE_RESET_METRES = 0.08
const PLANE_RESET_SLOPE = 0.15

/**
 * Minimum height above the analytic floor for a cell to survive as relief.
 *
 * The analytic floor's whole claim is that it is flat *by construction*, so
 * anything within the sensor's own noise of it is not information — 12 mm is
 * the same upper lock band the flatten path uses, i.e. 2σ against the measured
 * ~6 mm floor noise, and it stays well under the 25 mm reference object.
 */
export const PROUD_METRES = 0.012

const rayScratch = new Float32Array(3)

/**
 * Blends a fresh fit into the plane carried from last frame.
 *
 * Both are re-expressed about a common origin first: a plane's `height` is only
 * meaningful relative to its own `originX`/`originZ`, and those move with the
 * seed, so averaging the raw fields would mix two different quantities.
 */
export function blend(prior: GroundPlane, fresh: GroundPlane): GroundPlane {
  const priorHeight =
    prior.height +
    prior.slopeX * (fresh.originX - prior.originX) +
    prior.slopeZ * (fresh.originZ - prior.originZ)

  if (
    Math.abs(priorHeight - fresh.height) > PLANE_RESET_METRES ||
    Math.abs(prior.slopeX - fresh.slopeX) > PLANE_RESET_SLOPE ||
    Math.abs(prior.slopeZ - fresh.slopeZ) > PLANE_RESET_SLOPE
  ) {
    return fresh
  }

  return {
    ...fresh,
    height: priorHeight + (fresh.height - priorHeight) * PLANE_BLEND,
    slopeX: prior.slopeX + (fresh.slopeX - prior.slopeX) * PLANE_BLEND,
    slopeZ: prior.slopeZ + (fresh.slopeZ - prior.slopeZ) * PLANE_BLEND,
  }
}

/**
 * Deletes every cell that is not standing proud of `plane`, in place.
 *
 * This is the sidestep the whole field converged on — Google deletes depth
 * within 3 cm of a tracked plane, enva-xr never meshes its floor at all, Meta
 * routes physics to the Scene Mesh — and it is what makes the floor perfectly
 * flat instead of merely well filtered. Cells below the plane go too: an object
 * stands up, so anything beneath the floor is an artefact.
 *
 * Returns how many cells survived. Downstream this is invisible plumbing —
 * `metres[cell] = 0` is already the "no depth here" convention every scan
 * honours, so the quad emitter, the support fit and the ring probe all stop
 * seeing the floor without knowing why.
 */
export function stripToRelief(
  positions: Float32Array,
  metres: Float32Array,
  cells: number,
  plane: GroundPlane
): number {
  let proud = 0

  for (let cell = 0; cell < cells; cell += 1) {
    if (metres[cell] === 0) continue

    const offset = cell * 3
    const surface =
      plane.height +
      plane.slopeX * (positions[offset]! - plane.originX) +
      plane.slopeZ * (positions[offset + 2]! - plane.originZ)

    if (positions[offset + 1]! - surface > PROUD_METRES) proud += 1
    else metres[cell] = 0
  }

  return proud
}

/**
 * Fits the floor from the depth frame itself, snaps nearby cells onto it, and
 * fills holes it surrounds.
 *
 * `level` holds the plane normal at world +Y instead of fitting a tilt. It is
 * measurably worse (51% of cells locked against 77%) and the demo never passes
 * `true`; the parameter is kept because that comparison is how the default was
 * chosen. See `solveLevel`.
 */
export function lockGround(
  positions: Float32Array,
  metres: Float32Array,
  cells: number,
  gridWidth: number,
  gridHeight: number,
  seedX: number,
  seedZ: number,
  dirs: Float32Array,
  world: Float32Array,
  flags: Uint8Array,
  level: boolean,
  prior: GroundPlane | null
): GroundReport {
  const seed = fit(positions, metres, cells, seedX, seedZ, SEED_RADIUS, level)
  if (!seed) {
    return {
      plane: null,
      locked: 0,
      filled: 0,
      valid: countValid(metres, cells),
      radial: 0,
      spikes: 0,
    }
  }

  let plane: GroundPlane = {
    height: seed.height,
    slopeX: seed.slopeX,
    slopeZ: seed.slopeZ,
    originX: seedX,
    originZ: seedZ,
    rms: seed.rms,
    inliers: seed.samples,
  }

  for (const band of REFINE_BANDS) {
    const next = refit(positions, metres, cells, plane, band, level)
    if (!next) break
    plane = next
  }

  if (prior) plane = blend(prior, plane)

  flags.fill(0)
  let locked = 0
  let radial = 0
  let valid = 0
  const radiusSq = PLANE_RADIUS * PLANE_RADIUS

  const camX = world[12]!
  const camY = world[13]!
  const camZ = world[14]!
  const base = planeBase(plane, camX, camY, camZ)

  for (let cell = 0; cell < cells; cell += 1) {
    if (metres[cell] === 0) continue
    valid += 1

    const offset = cell * 3
    const dx = positions[offset]! - plane.originX
    const dz = positions[offset + 2]! - plane.originZ
    if (dx * dx + dz * dz > radiusSq) continue

    // The *decision* stays vertical: a height band is what protects a real
    // object from being swallowed, and it has to be measured in the axis the
    // climb threshold is measured in.
    const surface = plane.height + plane.slopeX * dx + plane.slopeZ * dz
    const height = positions[offset + 1]! - surface
    if (height > LOCK_BAND_ABOVE || height < -LOCK_BAND_BELOW) continue

    if (snap(positions, metres, cell, plane, dirs, world, base, surface, camX, camY, camZ)) {
      radial += 1
    }
    flags[cell] = GROUND
    locked += 1
  }

  const spikes = stripSpikes(
    positions,
    metres,
    gridWidth,
    gridHeight,
    plane,
    dirs,
    world,
    flags,
    base
  )
  locked += spikes

  const filled = fillHoles(
    positions,
    metres,
    gridWidth,
    gridHeight,
    plane,
    dirs,
    world,
    flags,
    base
  )
  return { plane, locked, filled, valid, radial, spikes }
}

/**
 * Moves one cell onto the plane. Returns true if it went along the camera ray.
 *
 * The correction goes along the ray rather than straight down because ARCore's
 * error is largely radial — it is a distance estimate, not a height estimate —
 * so on a floor seen at a grazing angle most of that error lands in XZ. Moving
 * the cell vertically puts it at the right height in the wrong place, and the
 * surface still reads as lumpy. A near-grazing ray whose intersection has run
 * away falls back to the vertical snap, which the lock band bounds by
 * construction.
 */
function snap(
  positions: Float32Array,
  metres: Float32Array,
  cell: number,
  plane: GroundPlane,
  dirs: Float32Array,
  world: Float32Array,
  base: number,
  surface: number,
  camX: number,
  camY: number,
  camZ: number
): boolean {
  const offset = cell * 3
  const distance = rayPlaneDepth(plane, dirs, offset, world, base, rayScratch)

  if (distance > 0 && Math.abs(distance - metres[cell]!) <= MAX_RAY_SHIFT) {
    const x = camX + distance * rayScratch[0]!
    const z = camZ + distance * rayScratch[2]!
    const slideX = x - positions[offset]!
    const slideZ = z - positions[offset + 2]!

    if (slideX * slideX + slideZ * slideZ <= MAX_LATERAL_SHIFT * MAX_LATERAL_SHIFT) {
      positions[offset] = x
      positions[offset + 1] = camY + distance * rayScratch[1]!
      positions[offset + 2] = z
      metres[cell] = distance
      return true
    }
  }

  positions[offset + 1] = surface
  return false
}

/**
 * One pass over the cells the band rejected, snapping those that are surrounded
 * by locked floor. Candidates are collected before any is applied, so a
 * stripped cell never becomes a neighbour for another and the pass cannot walk
 * inward into a real object from its edge.
 */
function stripSpikes(
  positions: Float32Array,
  metres: Float32Array,
  gridWidth: number,
  gridHeight: number,
  plane: GroundPlane,
  dirs: Float32Array,
  world: Float32Array,
  flags: Uint8Array,
  base: number
): number {
  const camX = world[12]!
  const camY = world[13]!
  const camZ = world[14]!

  let spikes = 0

  for (let j = 0; j < gridHeight; j += 1) {
    for (let i = 0; i < gridWidth; i += 1) {
      const cell = j * gridWidth + i
      if (metres[cell] === 0 || flags[cell] !== 0) continue

      const offset = cell * 3
      const dx = positions[offset]! - plane.originX
      const dz = positions[offset + 2]! - plane.originZ
      if (dx * dx + dz * dz > PLANE_RADIUS * PLANE_RADIUS) continue

      const surface = plane.height + plane.slopeX * dx + plane.slopeZ * dz
      const height = positions[offset + 1]! - surface
      if (height <= LOCK_BAND_ABOVE || height > SPIKE_MAX) continue

      let neighbours = 0
      if (i > 0 && flags[cell - 1] === GROUND) neighbours += 1
      if (i < gridWidth - 1 && flags[cell + 1] === GROUND) neighbours += 1
      if (j > 0 && flags[cell - gridWidth] === GROUND) neighbours += 1
      if (j < gridHeight - 1 && flags[cell + gridWidth] === GROUND) neighbours += 1
      if (neighbours < SPIKE_MIN_NEIGHBOURS) continue

      snap(positions, metres, cell, plane, dirs, world, base, surface, camX, camY, camZ)
      flags[cell] = PENDING
      spikes += 1
    }
  }

  if (spikes > 0) promotePending(flags)
  return spikes
}

/** Signed distance from the camera to the plane along world +Y. */
function planeBase(plane: GroundPlane, camX: number, camY: number, camZ: number): number {
  return (
    plane.height +
    plane.slopeX * (camX - plane.originX) +
    plane.slopeZ * (camZ - plane.originZ) -
    camY
  )
}

/**
 * Depth in metres at which this cell's camera ray meets the plane, or 0 if it
 * never does in front of the camera. The unit direction is written to `out`, in
 * the same unnormalised form the unprojection uses, so `camera + depth * out` is
 * the world point.
 */
function rayPlaneDepth(
  plane: GroundPlane,
  dirs: Float32Array,
  offset: number,
  world: Float32Array,
  base: number,
  out: Float32Array
): number {
  const rayX = dirs[offset]!
  const rayY = dirs[offset + 1]!

  const dx = world[0]! * rayX + world[4]! * rayY - world[8]!
  const dy = world[1]! * rayX + world[5]! * rayY - world[9]!
  const dz = world[2]! * rayX + world[6]! * rayY - world[10]!

  const denominator = dy - plane.slopeX * dx - plane.slopeZ * dz
  if (Math.abs(denominator) < 1e-6) return 0

  const distance = base / denominator
  if (distance <= 0) return 0

  out[0] = dx
  out[1] = dy
  out[2] = dz
  return distance
}

function countValid(metres: Float32Array, cells: number): number {
  let valid = 0
  for (let cell = 0; cell < cells; cell += 1) if (metres[cell] !== 0) valid += 1
  return valid
}

function promotePending(flags: Uint8Array): void {
  for (let cell = 0; cell < flags.length; cell += 1) {
    if (flags[cell] === PENDING) flags[cell] = GROUND
  }
}

function fit(
  positions: Float32Array,
  metres: Float32Array,
  cells: number,
  x: number,
  z: number,
  radius: number,
  level: boolean
) {
  const sums = scanSums(positions, metres, cells, x, z, radius)
  return level ? solveLevel(sums) : solvePlane(sums)
}

function refit(
  positions: Float32Array,
  metres: Float32Array,
  cells: number,
  plane: GroundPlane,
  band: number,
  level: boolean
): GroundPlane | null {
  const sums = emptySums()
  const radiusSq = PLANE_RADIUS * PLANE_RADIUS

  for (let cell = 0; cell < cells; cell += 1) {
    if (metres[cell] === 0) continue
    const offset = cell * 3
    const dx = positions[offset]! - plane.originX
    const dz = positions[offset + 2]! - plane.originZ
    if (dx * dx + dz * dz > radiusSq) continue

    const y = positions[offset + 1]!
    if (Math.abs(y - (plane.height + plane.slopeX * dx + plane.slopeZ * dz)) > band) continue
    addSample(sums, dx, dz, y)
  }

  const solved = level ? solveLevel(sums) : solvePlane(sums)
  if (!solved) return null

  return {
    height: solved.height,
    slopeX: solved.slopeX,
    slopeZ: solved.slopeZ,
    originX: plane.originX,
    originZ: plane.originZ,
    rms: solved.rms,
    inliers: solved.samples,
  }
}

/**
 * Grows the floor one cell-ring per pass into cells the sensor gave up on. The
 * neighbour test is what keeps it honest: a hole only fills if the floor already
 * surrounds it, so a genuine drop-off past the table edge stays empty.
 *
 * On device this has never fired — ARCore inpaints every pixel and reports no
 * holes at all. It is kept for hardware that does admit ignorance.
 */
function fillHoles(
  positions: Float32Array,
  metres: Float32Array,
  gridWidth: number,
  gridHeight: number,
  plane: GroundPlane,
  dirs: Float32Array,
  world: Float32Array,
  flags: Uint8Array,
  base: number
): number {
  const camX = world[12]!
  const camY = world[13]!
  const camZ = world[14]!

  let filled = 0

  for (let pass = 0; pass < FILL_PASSES; pass += 1) {
    let added = 0

    for (let j = 0; j < gridHeight; j += 1) {
      for (let i = 0; i < gridWidth; i += 1) {
        const cell = j * gridWidth + i
        if (metres[cell] !== 0) continue

        let neighbours = 0
        if (i > 0 && flags[cell - 1] === GROUND) neighbours += 1
        if (i < gridWidth - 1 && flags[cell + 1] === GROUND) neighbours += 1
        if (j > 0 && flags[cell - gridWidth] === GROUND) neighbours += 1
        if (j < gridHeight - 1 && flags[cell + gridWidth] === GROUND) neighbours += 1
        if (neighbours < FILL_MIN_NEIGHBOURS) continue

        const offset = cell * 3
        const distance = rayPlaneDepth(plane, dirs, offset, world, base, rayScratch)
        if (distance <= 0 || distance > MAX_FILL_METRES) continue

        positions[offset] = camX + distance * rayScratch[0]!
        positions[offset + 1] = camY + distance * rayScratch[1]!
        positions[offset + 2] = camZ + distance * rayScratch[2]!
        metres[cell] = distance
        flags[cell] = PENDING
        added += 1
      }
    }

    if (added === 0) break
    filled += added
    // Promoted only between passes, so growth is one ring at a time rather than
    // racing across the grid in scan order.
    promotePending(flags)
  }

  return filled
}
